import { actionTypes, histoScatterDateFormat, opButtons, plotTypeOptions } from './constants';
import { getApiActionCreator } from 'helpers/actionHelper';
import { apiCallStatus } from 'helpers/constants';
import { createParamMeasurementAssetObjTrend, getZoomingDates, createParamMeasurementAssetObjV2 } from '../../helpers';
import { pagesWithTrendChart } from '../../constants';
import { getPlotAPI, transformPlotRequestDataTrend, transformPlotRequestDataV2 } from './helpers';
import moment from 'moment';

const resetChartAction = () => {
    return {
        type: actionTypes.RESET_CHART,
    };
};

export const setActiveTab = (activeTab) => {
    return (dispatch) => {
        dispatch({ type: actionTypes.SET_ACTIVE_TAB, activeTab });
    };
};

export const clearStore = () => {
    return (dispatch) => {
        dispatch({ type: actionTypes.CLEAR_STORE });
    };
};

export const resetChart = () => {
    return (dispatch) => {
        dispatch(resetChartAction());
    };
};

const setChartDataLoadingAction = getApiActionCreator(actionTypes.SET_CHART_DATA_LOADING_STATUS);

const loadKPIDataAction = (data, kpi, component, kpiType, trendChartZoom, color = undefined) => {
    return {
        type: actionTypes.LOAD_KPI_DATA,
        data,
        kpi,
        component,
        color,
        kpiType,
        trendChartZoom: trendChartZoom
    };
};

export const removeKPIData = (data) => {
    return {
        type: actionTypes.REMOVE_SELECTED_KPI,
        data
    };
};

export const removeSelectedKPI = (data, selectedAsset, selectedKPIType, activeTab) => {
    return {
        type: actionTypes.REMOVE_SELECTED_KPI_CHKBOX,
        data,
        selectedAsset,
        selectedKPIType,
        activeTab
    };
};

export const setSelectedDates = (from, to) => {
    return (dispatch) => {
        dispatch({ type: actionTypes.SET_DATE_SELECTION, dateSelection: { from, to } });
    };
};

// Helper function to determine component KPI types
const getComponentKPITypes = (list, selectedKPIType, activeTab) => {
    const component = list.map((kpi) => kpi.component);
    const series = list.map((kpi) => kpi.kpi);
    if (selectedKPIType === 1) {
        return transformPlotRequestDataTrend(list);
    } else {
        return transformPlotRequestDataV2(component, series, activeTab === opButtons.scatterPlotV2);
    }
};

// Helper function to determine date range and zoom state
const getDateRange = (getState, isTrendChartZoomed, fromZoom, toZoom) => {
    let { fromDate, toDate, zoomed } = getZoomingDates(getState(), pagesWithTrendChart.crossAssetVisualization);
    if (isTrendChartZoomed) {
        fromDate = fromZoom;
        toDate = toZoom;
        zoomed = true;
    } else if (fromZoom && toZoom) {
        fromDate = fromZoom;
        toDate = toZoom;
        zoomed = false;
    }
    return { fromDate, toDate, zoomed };
};

// Helper function to process chart data
const processChartData = (dispatch, chartData, list, zoomed, fromDate, toDate, selectedKPIType) => {
    list.forEach((item) => {
        if (chartData && Array.isArray(chartData)) {
            const newValue = chartData?.find((o) => {
                const itemIdentifier = (item?.kpi?.timeseriesKey || '').toLowerCase();
                const oIdentifier = (o?.config?.timeseriesKey || '').toLowerCase();
                return itemIdentifier === oIdentifier && item?.componentKPIType[0]?.assetId === o?.assetId;
            });
            dispatch(loadKPIDataAction(newValue ? [newValue] : [], item?.kpi, item?.component, selectedKPIType, {
                isZoomed: zoomed,
                from: fromDate,
                to: toDate
            }));
        }
    });
};

// Helper function to fetch and process chart data
const fetchAndProcessChartData = (dispatch, getState, componentKPITypes, fromDate, toDate, list, zoomed, selectedKPIType) => {
    const apiKpiData = getPlotAPI(getState());
    apiKpiData(componentKPITypes, fromDate, toDate)
        .then((chartData) => {
            processChartData(dispatch, chartData, list, zoomed, fromDate, toDate, selectedKPIType);
            dispatch(setChartDataLoadingAction(apiCallStatus.SUCCESS));
        })
        .catch((err) => {
            dispatch(setChartDataLoadingAction(apiCallStatus.ERROR, null, err));
        });
};

export const getChartDataForNewDate = (list, isTrendChartZoomed, fromZoom, toZoom, selectedKPIType, activeTab) => {
    return (dispatch, getState) => {
        // Determine component KPI types
        const componentKPITypes = getComponentKPITypes(list, selectedKPIType, activeTab);
        // Determine date range and zoom state
        const { fromDate, toDate, zoomed } = getDateRange(getState, isTrendChartZoomed, fromZoom, toZoom);
        // Dispatch loading state
        dispatch(setChartDataLoadingAction(apiCallStatus.LOADING));
        const getTimeFormat = (date) => {
            if ([opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab)) {
                return moment(date).format(histoScatterDateFormat);
            } else {
                return date;
            }
        };
        // Fetch and process chart data
        fetchAndProcessChartData(dispatch, getState, componentKPITypes, getTimeFormat(fromDate), getTimeFormat(toDate), list, zoomed, selectedKPIType);
    };
};

export const getChartData = (kpi, component, selectedKPIType, activeTab) => {
    let formattedRequest;
    if (selectedKPIType === 1) {
        formattedRequest = createParamMeasurementAssetObjTrend(kpi, component);
    } else if (activeTab === opButtons.histogramV2) {
        formattedRequest = createParamMeasurementAssetObjV2(kpi, component);
    } else if (activeTab === opButtons.scatterPlotV2) {
        formattedRequest = createParamMeasurementAssetObjV2(kpi, component, true);
    } else if (kpi.plotTypeName === plotTypeOptions.histogramV2 || kpi.plotTypeName === plotTypeOptions.scatterPlotV2) {
        formattedRequest = createParamMeasurementAssetObjV2(kpi, component, kpi.plotTypeName === plotTypeOptions.scatterPlotV2);
    }
    return (dispatch, getState) => {

        const { chartView: { selectedKPIType } } = getState();
        const { fromDate, toDate, zoomed } = getZoomingDates(getState(), pagesWithTrendChart.crossAssetVisualization);
        const getTimeFormat = (date) => {
            if ([opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab)) {
                return moment(date).format(histoScatterDateFormat);
            } else {
                return date;
            }
        };
        dispatch(setChartDataLoadingAction(apiCallStatus.LOADING));
        const apiKpiData = getPlotAPI(getState());
        apiKpiData(formattedRequest, getTimeFormat(fromDate), getTimeFormat(toDate))
            .then((chartData) => {
                dispatch(loadKPIDataAction(chartData, kpi, component, selectedKPIType, {
                    isZoomed: zoomed,
                    from: getTimeFormat(fromDate),
                    to: getTimeFormat(toDate)
                }));
                dispatch(setChartDataLoadingAction(apiCallStatus.SUCCESS));
            })
            .catch((err) => {
                dispatch(setChartDataLoadingAction(apiCallStatus.ERROR, null, err));
            });
    };
};

export const setIsZoomed = (isTrendChartZoomed) => {
    return (dispatch) => {
        dispatch({ type: actionTypes.SET_ZOOMED, isTrendChartZoomed });
    };
};

export const resetChartData = (list, selectedKPIType) => {
    return (dispatch, getState) => {
        const { from, to } = getState().overview.dateSelection;
        dispatch(getChartDataForNewDate(list, false, from, to, selectedKPIType));
    };
};

export const zoomInToSelectedTrendKpiData = (list, isTrendChartZoomed, from, to, selectedKPIType) => {
    return (dispatch) => {
        return dispatch(getChartDataForNewDate(list, isTrendChartZoomed, from, to, selectedKPIType));
    };
};
